import { defaultHealthHabitsFormValues } from "@/D2C/features/health-quote/redux/health-quote/healthQuoteSlice";
import type { StepFormComponentProps, StepFormHandle } from "@/shared/stepper/types";
import { healthLifestyleSchema } from "@/shared/validation/healthQuoteSchemas";
import { zodResolver } from "@hookform/resolvers/zod";
import { forwardRef, useImperativeHandle, type ComponentProps } from "react";
import { useForm, type Resolver } from "react-hook-form";
import { useHealthMasterData } from "../../hooks/useHealthMasterData";
import { HealthHabitsFormValues } from "../../redux/type";
import { HealthHabitSelector } from "./HealthHabitSelector";
import { HealthQuestionsList } from "./HealthQuestionsList";

export const HealthHabitsStep = forwardRef<
  StepFormHandle<HealthHabitsFormValues>,
  StepFormComponentProps<
    HealthHabitsFormValues,
    {
      layoutClassName?: string;
      habitSelectorLayoutClassName?: string;
      questionsLayoutClassName?: string;
      showHealthTitleAndDescription?: boolean;
      isReadOnly?: boolean;
    }
  >
>(function HealthHabitsStep(
  {
    defaultValues,
    layoutClassName,
    habitSelectorLayoutClassName,
    questionsLayoutClassName,
    showHealthTitleAndDescription,
    isReadOnly = false,
  },
  ref
) {
  const { healthHabitOptions, healthQuestions } = useHealthMasterData();

  const form = useForm<HealthHabitsFormValues>({
    resolver: zodResolver(healthLifestyleSchema) as Resolver<
      HealthHabitsFormValues,
      any,
      HealthHabitsFormValues
    >,
    defaultValues:
      defaultValues ??
      ({
        ...defaultHealthHabitsFormValues,
      } as HealthHabitsFormValues),
    mode: "onChange",
    reValidateMode: "onChange",
  });

  useImperativeHandle(ref, () => ({
    submit: async () => form.trigger(undefined, { shouldFocus: true }),
    getValues: () => form.getValues(),
  }));

  const defaultShowHealthTitleAndDescription = showHealthTitleAndDescription ?? true;

  return (
    <fieldset disabled={isReadOnly} className={layoutClassName ?? "space-y-6"}>
      <div className="space-y-3">
        {defaultShowHealthTitleAndDescription && (
          <div>
            <h3 className="text-sm font-semibold text-theme-text">Health Habits</h3>
            <p className="text-xs text-theme-text-soft">
              Please select all habits that apply to you.
            </p>
          </div>
        )}

        <HealthHabitSelector
          control={form.control}
          options={healthHabitOptions as ComponentProps<typeof HealthHabitSelector>["options"]}
          layoutClassName={habitSelectorLayoutClassName}
        />
      </div>

      <div className="space-y-3">
        {defaultShowHealthTitleAndDescription && (
          <div>
            <h3 className="text-sm font-semibold text-theme-text">Quick Questions</h3>
            <p className="text-xs text-theme-text-soft">Please answer the following questions.</p>
          </div>
        )}

        <HealthQuestionsList
          control={form.control}
          questions={[...healthQuestions]}
          layoutClassName={questionsLayoutClassName}
        />
      </div>
    </fieldset>
  );
});
